/** @jsxImportSource ./simboluri-jsx */
import { useState } from "react";
import { numar, t, tx, useT } from "../i18n";

// Ghidul „Patru regimuri, patru strategii" — integrat la finalul Pasului 3.
// Conținutul complet al documentului de metodă, cu regimul care guvernează
// (din calculele pasului) evidențiat și preselectat, și cu procentele ρ
// calculate afișate pe fiecare regim.

const ORDINE = ["constructiv", "gravitational", "forfecare", "drift"] as const;
type Regim = (typeof ORDINE)[number];

// Numele și plajele celor patru regimuri, în limba de ACUM. Ca tabel de modul
// ar fi fost construit o dată, la încărcare, și ar fi înghețat limba primului
// utilizator care deschide pagina.
const meta = (): Record<Regim, { nr: string; titlu: string; range: string }> => ({
  constructiv: { nr: "01", titlu: t("struct.reg.meta_constructiv"),
                 range: t("struct.reg.range_constructiv") },
  gravitational: { nr: "02", titlu: t("struct.reg.gravitational"),
                   range: t("struct.reg.range_gravitational") },
  forfecare: { nr: "03", titlu: t("struct.reg.meta_forfecare"),
               range: t("struct.reg.range_forfecare") },
  drift: { nr: "04", titlu: t("struct.reg.meta_drift"),
           range: t("struct.reg.range_drift") },
});

/** Două zecimale fixe — punctul lui `toFixed` ar fi rămas punct și în română. */
const zec2 = { minimumFractionDigits: 2, maximumFractionDigits: 2 };

function pct(v: number | undefined): string {
  return v != null ? `ρ = ${numar(v * 100, zec2)}%` : "";
}

export default function RegimuriGhid({
  rho,
  guverneaza,
}: {
  rho: Record<string, number>;
  guverneaza: string;
}) {
  useT();
  const M = meta();
  const [activ, setActiv] = useState<Regim>(
    (ORDINE as readonly string[]).includes(guverneaza)
      ? (guverneaza as Regim)
      : "constructiv"
  );

  // zona de tranziție: două valori la sub 15% una de alta
  const sortate = ORDINE.map((k) => [k, rho[k] ?? 0] as const)
    .sort((a, b) => b[1] - a[1]);
  const tranzitie =
    sortate.length >= 2 && sortate[0][1] > 0 &&
    sortate[1][1] >= 0.85 * sortate[0][1];

  return (
    <div className="st-findings">
      <span className="rg-eyebrow">{t("struct.reg.ghid_eyebrow")}</span>
      <h3 style={{ fontSize: 17 }}>{t("struct.reg.patru_regimuri_titlu")}</h3>
      <p className="rg-lede">
        {t("struct.reg.limiteaza_fapt_cladire")}
      </p>

      {/* ------------------------- intro ------------------------- */}
      <span className="rg-sec-label">{t("struct.reg.punctul_de_plecare")}</span>
      <h4 className="rg-h2">{t("struct.reg.de_ce_patru_regimuri")}</h4>
      <p className="rg-p">{tx("struct.reg.cladire_nu_limitata")}</p>
      <p className="rg-p">
        {t("struct.reg.metoda_identificare_expr")}
      </p>
      <div className="rg-key">
        <span className="rg-lbl">{t("struct.reg.cele_patru_marimi")}</span>
        <pre className="rg-pre">{t("struct.reg.formule_rho")}</pre>
        <p className="rg-p" style={{ marginBottom: 0 }}>
          {t("struct.reg.daca_doua_valori")}
        </p>
      </div>
      {tranzitie && (
        <div className="rg-warnbox">
          <span className="rg-lbl">{t("struct.reg.zona_tranzitie_proiect")}</span>
          {t("struct.reg.tranzitie_doua", {
            a: M[sortate[0][0]].titlu, pa: numar(sortate[0][1] * 100, zec2),
            b: M[sortate[1][0]].titlu, pb: numar(sortate[1][1] * 100, zec2),
          })}
        </div>
      )}
      <div className="rg-note">
        <span className="rg-lbl">{t("struct.reg.context_normativ")}</span>
        {tx("struct.reg.documentul_se_refera")}
      </div>

      {/* -------------------- selectorul de regim -------------------- */}
      <div className="rg-strip">
        {ORDINE.map((k) => (
          <button
            key={k}
            className={`${activ === k ? "active" : ""} ${k === guverneaza ? "gov" : ""}`}
            onClick={() => setActiv(k)}
          >
            <span className="rg-n">{M[k].nr}</span>
            <span className="rg-t">{M[k].titlu}</span>
            <span className="rg-range">{M[k].range}</span>
            <span className="rg-rho">
              {pct(rho[k])}
              {k === guverneaza ? t("struct.reg.sageata_guverneaza") : ""}
            </span>
          </button>
        ))}
      </div>
      <div className="sg-muted" style={{ marginBottom: 14 }}>
        {t("struct.reg.pe_acest_proiect_guverneaza")}{" "}
        <strong style={{ color: "var(--danger)" }}>
          {M[(guverneaza as Regim) in M ? (guverneaza as Regim) : "constructiv"].titlu}
        </strong>{" "}
        {t("struct.reg.sectiunea_deschisa_jos")}
      </div>

      {activ === "constructiv" && <Constructiv gov={guverneaza === "constructiv"} rho={rho} />}
      {activ === "gravitational" && <Gravitational gov={guverneaza === "gravitational"} rho={rho} />}
      {activ === "forfecare" && <Forfecare gov={guverneaza === "forfecare"} rho={rho} />}
      {activ === "drift" && <Drift gov={guverneaza === "drift"} rho={rho} />}

      {/* -------------------- cadrul normativ în mișcare -------------------- */}
      <span className="rg-sec-label">{t("struct.reg.urmarit")}</span>
      <h4 className="rg-h2">{t("struct.reg.cadrul_normativ_miscare")}</h4>
      <p className="rg-p">
        {t("struct.reg.doua_schimbari_afecteaza")}
      </p>
      <h4 className="rg-h3">{t("struct.reg.np007_cod_nou")}</h4>
      <p className="rg-p">
        {t("struct.reg.aprobat_prin_ordin")}
      </p>
      <h4 className="rg-h3">{t("struct.reg.p100_2025_titlu")}</h4>
      <p className="rg-p">
        {t("struct.reg.aflat_proces_avizare")}
      </p>
      <div className="rg-note">
        <span className="rg-lbl">{t("struct.reg.implicatie_arhitectura_s")}</span>
        {tx("struct.reg.nu_codifica_valorile")}
      </div>

      {/* -------------------- rezumat -------------------- */}
      <span className="rg-sec-label">{t("struct.reg.rezumat")}</span>
      <h4 className="rg-h2">{t("struct.reg.tabelul_de_decizie")}</h4>
      <table className="sg-table" style={{ marginBottom: 14 }}>
        <thead>
          <tr>
            <th>{t("struct.reg.th_regim")}</th>
            <th>{t("struct.reg.structura_tipica")}</th>
            <th>{t("struct.reg.parghia_functioneaza")}</th>
            <th>{t("struct.reg.sa_faci")}</th>
          </tr>
        </thead>
        <tbody>
          <tr className={guverneaza === "constructiv" ? "rg-govrow" : ""}>
            <td><strong>{t("struct.reg.meta_constructiv")}</strong>{guverneaza === "constructiv" && <span className="rg-badge">{t("struct.reg.cazul_tau")}</span>}</td>
            <td>{t("struct.reg.rand_constructiv_structura")}</td>
            <td>{t("struct.reg.rand_constructiv_parghie")}</td>
            <td>{t("struct.reg.rand_constructiv_nu")}</td>
          </tr>
          <tr className={guverneaza === "gravitational" ? "rg-govrow" : ""}>
            <td><strong>{t("struct.reg.gravitational")}</strong>{guverneaza === "gravitational" && <span className="rg-badge">{t("struct.reg.cazul_tau")}</span>}</td>
            <td>{t("struct.reg.rand_grav_structura")}</td>
            <td>{t("struct.reg.rand_grav_parghie")}</td>
            <td>{t("struct.reg.sa_lungesti_sectiunile")}</td>
          </tr>
          <tr className={guverneaza === "forfecare" ? "rg-govrow" : ""}>
            <td><strong>{t("struct.reg.meta_forfecare")}</strong>{guverneaza === "forfecare" && <span className="rg-badge">{t("struct.reg.cazul_tau")}</span>}</td>
            <td>{t("struct.reg.nucleu_plus_pereti")}</td>
            <td>{t("struct.reg.rand_forf_parghie")}</td>
            <td>{t("struct.reg.sa_concentrezi_putini")}</td>
          </tr>
          <tr className={guverneaza === "drift" ? "rg-govrow" : ""}>
            <td><strong>{t("struct.reg.meta_drift")}</strong>{guverneaza === "drift" && <span className="rg-badge">{t("struct.reg.cazul_tau")}</span>}</td>
            <td>{t("struct.reg.rand_drift_structura")}</td>
            <td>{t("struct.reg.rand_drift_parghie")}</td>
            <td>{tx("struct.reg.rand_drift_nu")}</td>
          </tr>
        </tbody>
      </table>

      <h4 className="rg-h3">{t("struct.reg.trei_constante_titlu")}</h4>
      <ul className="rg-ul">
        <li>{tx("struct.reg.constanta_ltyp")}</li>
        <li>{tx("struct.reg.constanta_vrd")}</li>
        <li>{t("struct.reg.constanta_concentrare")}</li>
      </ul>
      <p className="rg-p">
        {t("struct.reg.testul_validare_ruleaza")}
      </p>

      {/* -------------------- referințe -------------------- */}
      <span className="rg-sec-label">{t("struct.reg.surse")}</span>
      <h4 className="rg-h2">{t("struct.reg.referinte")}</h4>
      <ol className="rg-refs">
        <li>{t("struct.reg.ref_p100")}{" "}
          <a href="https://legislatie.just.ro/Public/DetaliiDocument/219873" target="_blank" rel="noreferrer">legislatie.just.ro</a></li>
        <li>{t("struct.reg.ref_cr2")}{" "}
          <a href="https://www.encipedia.org/articole/proiectare/coduri-de-proiectare/cr-2-1-1-1-2013-cod-de-proiectare-a-constructiilor-cu-pereti-structurali-de-beton-armat" target="_blank" rel="noreferrer">encipedia.org</a></li>
        <li>{t("struct.reg.ref_np007")}{" "}
          <a href="https://legislatie.just.ro/public/DetaliiDocument/299715" target="_blank" rel="noreferrer">legislatie.just.ro</a></li>
        <li>{t("struct.reg.sr_en_1998")}</li>
        <li>{tx("struct.reg.ref_comentarii_cap5")}{" "}
          <a href="https://www.encipedia.org/articole/proiectare/coduri-de-proiectare/cod-de-proiectare-seismica-p100-1-2013/capitolul-5-prevederi-specifice-constructiilor-de-beton.html" target="_blank" rel="noreferrer">encipedia.org</a></li>
        <li>{t("struct.reg.comparatie_a_factorilor")}</li>
        <li>{t("struct.reg.ref_limite_nu")}{" "}
          <a href="https://www.hkie.org.hk/hkietransactions/upload/2019-01-15/Ductility_design_of_reinforced_concrete_shear_walls_with_the_consideration_of_axial_compression_ratio.pdf" target="_blank" rel="noreferrer">hkie.org.hk</a></li>
        <li>{t("struct.reg.ref_zonare")}{" "}
          <a href="https://www.mdpi.com/2076-3417/12/2/649" target="_blank" rel="noreferrer">mdpi.com</a></li>
        <li>{t("struct.reg.densitatea_minima_pereti")}</li>
        <li>{tx("struct.reg.ref_zhang")}{" "}
          <a href="https://www.sciencedirect.com/science/article/abs/pii/S0141029616315115" target="_blank" rel="noreferrer">sciencedirect.com</a></li>
        <li>{t("struct.reg.ref_doua_etape")}{" "}
          <a href="https://www.sciencedirect.com/science/article/abs/pii/S2352012421008857" target="_blank" rel="noreferrer">sciencedirect.com</a></li>
        <li>{t("struct.reg.ref_tabu")}{" "}
          <a href="https://www.sciencedirect.com/science/article/abs/pii/S0045794921000687" target="_blank" rel="noreferrer">sciencedirect.com</a></li>
        <li>{t("struct.reg.ref_bim")}{" "}
          <a href="https://onlinelibrary.wiley.com/doi/10.1002/suco.70230" target="_blank" rel="noreferrer">onlinelibrary.wiley.com</a></li>
        <li>{t("struct.reg.ref_aicps")}{" "}
          <a href="https://www.aicps.ro/stiri/tehnic/punct-de-vedere-al-aicps-cod-de-proiectare-seismica-partea-i-prevederi-de-proiectare-pentru-cladiri-indicativ-p-100-1-2025-transmis-la-ministerul-dezvoltarii" target="_blank" rel="noreferrer">aicps.ro</a></li>
        <li>{t("struct.reg.ref_oar")}{" "}
          <a href="https://oar.archi/stiri/pozitia-ordinului-arhitectilor-din-romania-cu-privire-la-proiectul-de-reglementare-p100-1-2025-proiectare-seismica/" target="_blank" rel="noreferrer">oar.archi</a></li>
      </ol>
      <div className="rg-foot">
        {t("struct.reg.document_lucru_echipa")}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Titlul unui regim, cu ρ-ul calculat și badge-ul „guvernează"
// ---------------------------------------------------------------------------

function TitluRegim({ k, gov, rho }: { k: Regim; gov: boolean; rho: Record<string, number> }) {
  useT();
  const M = meta();
  return (
    <>
      <span className="rg-sec-label">
        {t("struct.reg.regim_nr_range", { nr: M[k].nr, range: M[k].range })}
      </span>
      <h4 className="rg-h2">
        {M[k].titlu}{" "}
        <span className="sg-mono" style={{ fontSize: 13 }}>{pct(rho[k])}</span>
        {gov ? (
          <span className="rg-badge">{t("struct.reg.guverneaza_proiect")}</span>
        ) : (
          <span className="rg-badge rg-badge-muted">{t("struct.reg.guverneaza_aici")}</span>
        )}
      </h4>
    </>
  );
}

// ---------------------------------------------------------------------------
// 01 — Constructiv
// ---------------------------------------------------------------------------

function Constructiv({ gov, rho }: { gov: boolean; rho: Record<string, number> }) {
  useT();
  return (
    <div>
      <TitluRegim k="constructiv" gov={gov} rho={rho} />
      <p className="rg-p">
        {t("struct.reg.nimic_seismic_strange")}
      </p>

      <h4 className="rg-h3">{t("struct.reg.intampla_fizic")}</h4>
      <p className="rg-p">
        {tx("struct.reg.cererea_cubul")}
      </p>
      <ul className="rg-ul">
        <li><b>{t("struct.reg.rezemarea_planseului")}</b> {t("struct.reg.fara_grinzi_vizibile")}</li>
        <li><b>{t("struct.reg.ierarhizarea_capacitatii")}</b> {t("struct.reg.grinda_trebuie_sa")}</li>
        <li><b>{t("struct.reg.efectul_de_cutie")}</b> {t("struct.reg.peretii_lungi_trebuie")}</li>
      </ul>

      <h4 className="rg-h3">{t("struct.reg.arata_structura")}</h4>
      <figure className="rg-fig">
        <svg viewBox="0 0 900 330" role="img">
          <rect x="60" y="45" width="620" height="230" className="rg-svg-thin" />
          <line x1="270" y1="45" x2="270" y2="275" className="rg-svg-thin" />
          <line x1="470" y1="45" x2="470" y2="275" className="rg-svg-thin" />
          <line x1="60" y1="160" x2="680" y2="160" className="rg-svg-thin" />
          <text className="rg-svg-ts" x="165" y="108" textAnchor="middle">{t("struct.reg.camera")}</text>
          <text className="rg-svg-ts" x="370" y="108" textAnchor="middle">{t("struct.reg.camera")}</text>
          <text className="rg-svg-ts" x="575" y="108" textAnchor="middle">{t("struct.reg.camera")}</text>
          <text className="rg-svg-ts" x="165" y="225" textAnchor="middle">{t("struct.reg.camera")}</text>
          <text className="rg-svg-ts" x="370" y="225" textAnchor="middle">{t("struct.reg.hol")}</text>
          <text className="rg-svg-ts" x="575" y="225" textAnchor="middle">{t("struct.reg.camera")}</text>
          <rect className="rg-svg-fill" x="52" y="37" width="30" height="16" /><rect className="rg-svg-fill" x="52" y="37" width="16" height="30" />
          <rect className="rg-svg-fill" x="658" y="37" width="30" height="16" /><rect className="rg-svg-fill" x="672" y="37" width="16" height="30" />
          <rect className="rg-svg-fill" x="52" y="267" width="30" height="16" /><rect className="rg-svg-fill" x="52" y="253" width="16" height="30" />
          <rect className="rg-svg-fill" x="658" y="267" width="30" height="16" /><rect className="rg-svg-fill" x="672" y="253" width="16" height="30" />
          <rect className="rg-svg-fill" x="248" y="37" width="44" height="16" /><rect className="rg-svg-fill" x="262" y="37" width="16" height="30" />
          <rect className="rg-svg-fill" x="448" y="37" width="44" height="16" /><rect className="rg-svg-fill" x="462" y="37" width="16" height="30" />
          <rect className="rg-svg-fill" x="248" y="267" width="44" height="16" /><rect className="rg-svg-fill" x="262" y="253" width="16" height="30" />
          <rect className="rg-svg-fill" x="448" y="267" width="44" height="16" /><rect className="rg-svg-fill" x="462" y="253" width="16" height="30" />
          <rect className="rg-svg-fill" x="52" y="138" width="30" height="44" />
          <rect className="rg-svg-fill" x="658" y="138" width="30" height="44" />
          <rect className="rg-svg-fill" x="248" y="152" width="44" height="16" /><rect className="rg-svg-fill" x="262" y="138" width="16" height="44" />
          <rect className="rg-svg-fill" x="448" y="152" width="44" height="16" /><rect className="rg-svg-fill" x="462" y="138" width="16" height="44" />
          <rect className="rg-svg-fill" x="150" y="152" width="44" height="16" />
          <rect className="rg-svg-fill" x="560" y="152" width="44" height="16" />
          <line x1="60" y1="300" x2="270" y2="300" className="rg-svg-thin" />
          <line x1="60" y1="294" x2="60" y2="306" className="rg-svg-thin" />
          <line x1="270" y1="294" x2="270" y2="306" className="rg-svg-thin" />
          <text className="rg-svg-tm" x="165" y="318" textAnchor="middle">4,00 – 5,00 m</text>
          <line className="rg-svg-dash" x1="700" y1="45" x2="782" y2="60" />
          <text className="rg-svg-ts" x="788" y="64">{t("struct.reg.colt_l")}</text>
          <line className="rg-svg-dash" x1="700" y1="160" x2="782" y2="150" />
          <text className="rg-svg-ts" x="788" y="154">{t("struct.reg.capat_dreptunghi")}</text>
          <line className="rg-svg-dash" x1="492" y1="160" x2="782" y2="240" />
          <text className="rg-svg-ts" x="788" y="244">{t("struct.reg.intersectie_t")}</text>
        </svg>
        <figcaption className="rg-figcap">
          <b>{t("struct.reg.elementele_urmeaza_nodur")}</b> {t("struct.reg.grosimea_fiecarui_elemen")}
        </figcaption>
      </figure>

      <h4 className="rg-h3">{t("struct.reg.ce_spune_normativul")}</h4>
      <p className="rg-p">
        {t("struct.reg.aici_cele_doua")}
      </p>
      <table className="sg-table" style={{ marginBottom: 12 }}>
        <thead>
          <tr><th style={{ width: "26%" }}>{t("struct.reg.th_subiect")}</th><th style={{ width: "37%" }}>{t("struct.reg.romania")}</th><th>Eurocode</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>{t("struct.reg.zidarie_confinata")}</td>
            <td>{tx("struct.reg.densitate_minima_p")}</td>
            <td>{t("struct.reg.en1996_mai_putin")}</td>
          </tr>
          <tr>
            <td>{tx("struct.reg.valoare_tipica_p")}</td>
            <td className="sg-mono">{t("struct.reg.6_0_directie")}</td>
            <td className="sg-mono">{t("struct.reg.parametru_national")}</td>
          </tr>
          <tr>
            <td>{t("struct.reg.goluri_perete")}</td>
            <td>{t("struct.reg.raport_goluri_plinuri")}</td>
            <td>{t("struct.reg.reguli_geometrice_minime")}</td>
          </tr>
          <tr>
            <td>{t("struct.reg.cadre_de_beton")}</td>
            <td>{t("struct.reg.np007_impreuna")}</td>
            <td>EN 1992-1-1 + EN 1998-1 cap. 5</td>
          </tr>
        </tbody>
      </table>
      <p className="rg-p">
        {tx("struct.reg.densitatea_de_6")}
      </p>

      <h4 className="rg-h3">{t("struct.reg.de_implementat")}</h4>
      <pre className="rg-pre">{t("struct.reg.algoritm_constructiv")}</pre>

      <h4 className="rg-h3">{t("struct.reg.strategia_de_optimizare")}</h4>
      <div className="rg-key">
        <span className="rg-lbl">{t("struct.reg.optimizarea_eliminare")}</span>
        <p className="rg-p" style={{ marginBottom: 0 }}>
          {tx("struct.reg.nu_exista_spatiu_cautare")}
        </p>
      </div>
      <pre className="rg-pre">{`for nod in sortate_dupa_economie_descrescator:
    if toate_grinzile_noi(nod).h <= h_disponibila \\
       and ierarhizare_ok_fara(nod) \\
       and excentricitate_ok_fara(nod):
        elimina(nod)`}</pre>
      <p className="rg-p">
        {tx("struct.reg.problema_de_pruning")}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 02 — Gravitațional
// ---------------------------------------------------------------------------

function Gravitational({ gov, rho }: { gov: boolean; rho: Record<string, number> }) {
  useT();
  return (
    <div>
      <TitluRegim k="gravitational" gov={gov} rho={rho} />
      <p className="rg-p">
        {t("struct.reg.te_limiteaza_greutatea")}
      </p>

      <h4 className="rg-h3">{t("struct.reg.intampla_fizic")}</h4>
      <p className="rg-p">
        {t("struct.reg.marimea_de_control_nud")}{" "}
        <b>{t("struct.reg.forta_axiala_normalizata")}</b>:
      </p>
      <pre className="rg-pre">{t("struct.reg.formula_nud")}</pre>
      <p className="rg-p">
        {tx("struct.reg.fractiune_adimensionala")}
      </p>

      <figure className="rg-fig">
        <svg viewBox="0 0 900 300" role="img">
          <text className="rg-svg-ts" x="60" y="36">{t("struct.reg.svg_suprafata_aferenta")}</text>
          <rect className="rg-svg-fill4" x="60" y="60" width="70" height="70" />
          <text className="rg-svg-tm" x="95" y="100" textAnchor="middle">20 m²</text>
          <text className="rg-svg-ts" x="95" y="150" textAnchor="middle">P+2</text>
          <text className="rg-svg-tm" x="95" y="168" textAnchor="middle">N ≈ 840 kN</text>
          <rect className="rg-svg-fill" x="60" y="196" width="44" height="44" />
          <text className="rg-svg-tm" x="82" y="262" textAnchor="middle">30×30</text>
          <text className="rg-svg-tm" x="82" y="278" textAnchor="middle">nu_d 0,47</text>
          <rect className="rg-svg-fill4" x="290" y="60" width="70" height="70" />
          <text className="rg-svg-tm" x="325" y="100" textAnchor="middle">20 m²</text>
          <text className="rg-svg-ts" x="325" y="150" textAnchor="middle">P+5</text>
          <text className="rg-svg-tm" x="325" y="168" textAnchor="middle">N ≈ 1680 kN</text>
          <rect className="rg-svg-fill" x="290" y="196" width="62" height="62" />
          <text className="rg-svg-tm" x="321" y="278" textAnchor="middle">45×45 · nu_d 0,41</text>
          <rect className="rg-svg-fill4" x="530" y="60" width="70" height="70" />
          <text className="rg-svg-tm" x="565" y="100" textAnchor="middle">20 m²</text>
          <text className="rg-svg-ts" x="565" y="150" textAnchor="middle">P+10</text>
          <text className="rg-svg-tm" x="565" y="168" textAnchor="middle">N ≈ 3080 kN</text>
          <rect className="rg-svg-fill" x="530" y="196" width="84" height="84" />
          <text className="rg-svg-tm" x="660" y="240">60×60 · nu_d 0,43</text>
          <line className="rg-svg-dash" x1="700" y1="60" x2="700" y2="130" />
          <text className="rg-svg-ts" x="712" y="88">{t("struct.reg.aceeasi_suprafata")}</text>
          <text className="rg-svg-ts" x="712" y="106">{t("struct.reg.aferenta_alt_numar")}</text>
          <text className="rg-svg-ts" x="712" y="124">{t("struct.reg.svg_de_niveluri")}</text>
        </svg>
        <figcaption className="rg-figcap">
          <b>{t("struct.reg.sectiunea_creste_liniar")}</b>{" "}
          {tx("struct.reg.figcap_aria_aferenta")}
        </figcaption>
      </figure>

      <h4 className="rg-h3">{t("struct.reg.ce_spune_normativul")}</h4>
      <p className="rg-p">
        {tx("struct.reg.limitele_nud_coincid")}
      </p>
      <table className="sg-table" style={{ marginBottom: 12 }}>
        <thead>
          <tr><th>{t("struct.reg.th_element")}</th><th>DCH</th><th>DCM</th><th>DCL</th></tr>
        </thead>
        <tbody>
          <tr><td>{t("struct.reg.stalp")}</td><td className="sg-mono">nu_d ≤ 0,55</td><td className="sg-mono">nu_d ≤ 0,65</td><td className="sg-mono">{t("struct.reg.fara_limita")}</td></tr>
          <tr><td>{t("struct.reg.perete_ductil")}</td><td className="sg-mono">nu_d ≤ 0,35</td><td className="sg-mono">nu_d ≤ 0,40</td><td className="sg-mono">{t("struct.reg.fara_limita")}</td></tr>
        </tbody>
      </table>
      <div className="rg-warnbox">
        <span className="rg-lbl">{t("struct.reg.discontinuitate_titlu")}</span>
        {tx("struct.reg.element_devine_perete")}
      </div>
      <p className="rg-p">
        {tx("struct.reg.diferenta_reala_eurocode")}
      </p>

      <h4 className="rg-h3">{t("struct.reg.de_implementat")}</h4>
      <pre className="rg-pre">{t("struct.reg.algoritm_gravitational")}</pre>

      <h4 className="rg-h3">{t("struct.reg.strategia_de_optimizare")}</h4>
      <p className="rg-p">{t("struct.reg.doua_parghii_ordinea")}</p>
      <ol className="rg-ul">
        <li>
          <b>{t("struct.reg.redu_suprafata_aferenta")}</b>{" "}
          {t("struct.reg.parghia_adauga_stalp")}
        </li>
        <li>
          <b>{t("struct.reg.creste_clasa_betonului")}</b>{" "}
          {tx("struct.reg.parghia_clasa_betonului")}
        </li>
      </ol>
      <p className="rg-p">
        <b>{t("struct.reg.lungirea_sectiunii_ajuta")}</b> {t("struct.reg.conteaza_doar_aria")}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 03 — Forfecare
// ---------------------------------------------------------------------------

function Forfecare({ gov, rho }: { gov: boolean; rho: Record<string, number> }) {
  useT();
  return (
    <div>
      <TitluRegim k="forfecare" gov={gov} rho={rho} />
      <p className="rg-p">
        {t("struct.reg.forfecare_intro")}
      </p>

      <h4 className="rg-h3">{t("struct.reg.intampla_fizic")}</h4>
      <p className="rg-p">{t("struct.reg.perete_solicitat_lateral")}</p>
      <figure className="rg-fig">
        <svg viewBox="0 0 900 300" role="img">
          <defs>
            <marker id="rg-ar" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M2 1L8 5L2 9" fill="none" stroke="#20201e" strokeWidth="1.4" strokeLinecap="round" />
            </marker>
          </defs>
          <line x1="120" y1="52" x2="182" y2="52" className="rg-svg-line" markerEnd="url(#rg-ar)" />
          <text className="rg-svg-ts" x="151" y="42" textAnchor="middle">F</text>
          <rect className="rg-svg-fill" x="190" y="48" width="70" height="190" />
          <line x1="192" y1="222" x2="240" y2="222" className="rg-svg-acc" />
          <line x1="192" y1="206" x2="228" y2="206" className="rg-svg-acc" />
          <line x1="192" y1="190" x2="216" y2="190" className="rg-svg-acc" />
          <line x1="175" y1="240" x2="275" y2="240" className="rg-svg-line" />
          <text className="rg-svg-t" x="225" y="268" textAnchor="middle">{t("struct.reg.svg_zvelt")}</text>
          <text className="rg-svg-ts" x="225" y="286" textAnchor="middle">{t("struct.reg.svg_fisuri_orizontale")}</text>
          <line x1="430" y1="52" x2="492" y2="52" className="rg-svg-line" markerEnd="url(#rg-ar)" />
          <text className="rg-svg-ts" x="461" y="42" textAnchor="middle">F</text>
          <rect className="rg-svg-fill3" x="500" y="48" width="190" height="190" />
          <line x1="516" y1="228" x2="672" y2="62" className="rg-svg-acc" />
          <line x1="556" y1="230" x2="678" y2="100" className="rg-svg-acc" />
          <line x1="512" y1="188" x2="632" y2="58" className="rg-svg-acc" />
          <line x1="485" y1="240" x2="705" y2="240" className="rg-svg-line" />
          <text className="rg-svg-t" x="595" y="268" textAnchor="middle">{t("struct.reg.svg_scurt_hl")}</text>
          <text className="rg-svg-ts" x="595" y="286" textAnchor="middle">{t("struct.reg.svg_fisuri_diagonale")}</text>
          <line className="rg-svg-dash" x1="720" y1="120" x2="770" y2="120" />
          <text className="rg-svg-ts" x="778" y="112">{t("struct.reg.forfecarea")}</text>
          <text className="rg-svg-ts" x="778" y="130">{t("struct.reg.avertizeaza_inainte")}</text>
          <text className="rg-svg-ts" x="778" y="148">{t("struct.reg.sa_cedeze")}</text>
        </svg>
        <figcaption className="rg-figcap">
          <b>{t("struct.reg.doua_mecanisme_doua")}</b> {t("struct.reg.cedarea_incovoiere_progr")}
        </figcaption>
      </figure>

      <p className="rg-p">
        {tx("struct.reg.marimea_de_control_v")}
      </p>

      <div className="rg-key">
        <span className="rg-lbl">{t("struct.reg.amplificarea_fortei_taie")}</span>
        <p className="rg-p" style={{ marginBottom: 0 }}>
          {tx("struct.reg.forta_dimensionare_majorata")}
        </p>
      </div>

      <h4 className="rg-h3">{t("struct.reg.ce_spune_normativul")}</h4>
      <table className="sg-table" style={{ marginBottom: 12 }}>
        <thead>
          <tr><th style={{ width: "28%" }}>{t("struct.reg.th_subiect")}</th><th style={{ width: "36%" }}>{t("struct.reg.romania")}</th><th>Eurocode</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>{t("struct.reg.cod_dedicat_peretilor")}</td>
            <td>{t("struct.reg.cr2_prevederi_proprii")}</td>
            <td>{t("struct.reg.en1998_cap5_impreuna")}</td>
          </tr>
          <tr>
            <td>{t("struct.reg.factor")} <code>k_w</code> {t("struct.reg.pereti_scurti")}</td>
            <td>{tx("struct.reg.kw_se_aplica")}</td>
            <td>{t("struct.reg.acelasi_mecanism_dual")}</td>
          </tr>
          <tr>
            <td>{t("struct.reg.definitia_peretelui_cupl")}</td>
            <td>{tx("struct.reg.grinzile_cuplare_30")} {t("struct.reg.momentele_baza_montantil")}</td>
            <td>{t("struct.reg.criteriu_echivalent_momente")}</td>
          </tr>
          <tr>
            <td>{t("struct.reg.grinzi_cuplare_scurte")}</td>
            <td>{t("struct.reg.armare_carcase_diagonale")}</td>
            <td>{t("struct.reg.aceeasi_solutie")}</td>
          </tr>
        </tbody>
      </table>
      <p className="rg-p">
        {tx("struct.reg.factorul_kw_bucla")}
      </p>

      <h4 className="rg-h3">{t("struct.reg.de_implementat")}</h4>
      <pre className="rg-pre">{t("struct.reg.algoritm_forfecare")}</pre>

      <h4 className="rg-h3">{t("struct.reg.strategia_de_optimizare")}</h4>
      <ul className="rg-ul">
        <li>
          <b>{t("struct.reg.clasa_beton_inaintea")}</b> {t("struct.reg.capacitatea_forfecare_cr")}
        </li>
        <li>
          <b>{t("struct.reg.imparte_concentra")}</b> {t("struct.reg.aria_aditiva_deci")}
        </li>
        <li>
          <b>{t("struct.reg.verifica_transferul_pere")}</b>{" "}
          {tx("struct.reg.transferul_in_perete")}
        </li>
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 04 — Drift
// ---------------------------------------------------------------------------

function Drift({ gov, rho }: { gov: boolean; rho: Record<string, number> }) {
  useT();
  return (
    <div>
      <TitluRegim k="drift" gov={gov} rho={rho} />
      <p className="rg-p">
        {t("struct.reg.drift_intro")}
      </p>

      <h4 className="rg-h3">{t("struct.reg.intampla_fizic")}</h4>
      <p className="rg-p">
        {tx("struct.reg.se_verifica_drift")}
      </p>

      <div className="rg-key">
        <span className="rg-lbl">{t("struct.reg.cele_doua_verificari")}</span>
        <pre className="rg-pre">{t("struct.reg.cele_doua_verificari_pre")}</pre>
        <p className="rg-p" style={{ marginBottom: 0 }}>
          {tx("struct.reg.dre_deplasarea")}
        </p>
      </div>

      <p className="rg-p">{t("struct.reg.sursa_cererii_rigiditate")}</p>
      <pre className="rg-pre">{t("struct.reg.formula_ireq")}</pre>
      <p className="rg-p">
        {tx("struct.reg.cererea_n3_oferta_l3")}{" "}
        <b>{t("struct.reg.lungimea_elementelor_bat")}</b>
      </p>

      <figure className="rg-fig">
        <svg viewBox="0 0 900 320" role="img">
          <text className="rg-svg-ts" x="60" y="34">{t("struct.reg.acelasi_i_beton")}</text>
          <text className="rg-svg-t" x="250" y="72" textAnchor="end">{t("struct.reg.2_pereti_8")}</text>
          <rect className="rg-svg-fill" x="262" y="55" width="120" height="24" />
          <text className="rg-svg-tm" x="392" y="72">4,8 m²</text>
          <text className="rg-svg-t" x="250" y="126" textAnchor="end">{t("struct.reg.4_pereti_6")}</text>
          <rect className="rg-svg-fill" x="262" y="109" width="190" height="24" />
          <text className="rg-svg-tm" x="462" y="126">7,6 m²</text>
          <text className="rg-svg-t" x="250" y="180" textAnchor="end">{t("struct.reg.8_pereti_5")}</text>
          <rect className="rg-svg-fill" x="262" y="163" width="302" height="24" />
          <text className="rg-svg-tm" x="574" y="180">12,1 m²</text>
          <text className="rg-svg-t" x="250" y="234" textAnchor="end">{t("struct.reg.16_pereti_4")}</text>
          <rect className="rg-svg-fill3" x="262" y="217" width="480" height="24" />
          <text className="rg-svg-tm" x="752" y="234">19,2 m²</text>
          <line x1="262" y1="256" x2="262" y2="46" className="rg-svg-thin" />
          <text className="rg-svg-ts" x="450" y="286" textAnchor="middle">{t("struct.reg.svg_dublare_beton")}</text>
        </svg>
        <figcaption className="rg-figcap">
          <b>{t("struct.reg.eficienta_scade_patratic")}</b>{" "}
          {tx("struct.reg.figcap_eficienta_patratic")}
        </figcaption>
      </figure>

      <h4 className="rg-h3">{t("struct.reg.ce_spune_normativul")}</h4>
      <p className="rg-p">
        {t("struct.reg.aici_cele_mari")}
      </p>
      <table className="sg-table" style={{ marginBottom: 12 }}>
        <thead>
          <tr><th style={{ width: "28%" }}>{t("struct.reg.th_subiect")}</th><th style={{ width: "36%" }}>{t("struct.reg.romania")}</th><th>Eurocode</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>{tx("struct.reg.perioada_de_colt")}</td>
            <td>{tx("struct.reg.trei_valori_teritoriu")}{t("struct.reg.bucuresti_are_1")}</td>
            <td>{t("struct.reg.uzual_04_08")}</td>
          </tr>
          <tr>
            <td>{t("struct.reg.consecinta")}</td>
            <td>{t("struct.reg.palierul_pana_16")} <b>{t("struct.reg.flexibilizarea_reduce_fo")}</b></td>
            <td>{t("struct.reg.structurile_flexibile_aluneca")}</td>
          </tr>
          <tr>
            <td>{t("struct.reg.acceleratia_proiectare")}</td>
            <td>{t("struct.reg.bucuresti_ag_030")}</td>
            <td>{t("struct.reg.parametru_national_475")}</td>
          </tr>
          <tr>
            <td>{t("struct.reg.q_cadre_pereti")}</td>
            <td className="sg-mono">5 · alpha_u/alpha_1</td>
            <td className="sg-mono">4,5 · alpha_u/alpha_1</td>
          </tr>
          <tr>
            <td>{t("struct.reg.aceleasi_dcm")}</td>
            <td className="sg-mono">3,5 · alpha_u/alpha_1</td>
            <td className="sg-mono">3,0 · alpha_u/alpha_1</td>
          </tr>
          <tr>
            <td>{t("struct.reg.pereti_zvelti_necuplati")}</td>
            <td className="sg-mono">4 · alpha_u/alpha_1</td>
            <td className="sg-mono">4 · alpha_u/alpha_1</td>
          </tr>
          <tr>
            <td>{t("struct.reg.neregularitate_verticala")}</td>
            <td>{t("struct.reg.q_reduce_20")}</td>
            <td>q = 0,8 · q_0</td>
          </tr>
        </tbody>
      </table>
      <div className="rg-warnbox">
        <span className="rg-lbl">{t("struct.reg.inseamna_algoritm")}</span>
        {t("struct.reg.in_restul_europei")}{" "}
        <b>{t("struct.reg.bucuresti_aceasta_strate")}</b> {t("struct.reg.structura_perioada_1")}
      </div>
      <p className="rg-p">
        {tx("struct.reg.valorile_alpha")}
      </p>

      <h4 className="rg-h3">{t("struct.reg.de_implementat")}</h4>
      <pre className="rg-pre">{t("struct.reg.algoritm_drift")}</pre>

      <h4 className="rg-h3">{t("struct.reg.strategia_de_optimizare")}</h4>
      <p className="rg-p">
        {t("struct.reg.acesta_singurul_regim")}
      </p>

      <h4 className="rg-h4">{t("struct.reg.metoda_ground_structure")}</h4>
      <p className="rg-p">{tx("struct.reg.amprenta_discretizata")}</p>

      <figure className="rg-fig">
        <svg viewBox="0 0 900 300" role="img">
          <g className="rg-svg-dash">
            <line x1="80" y1="50" x2="200" y2="50" /><line x1="200" y1="50" x2="320" y2="50" /><line x1="320" y1="50" x2="440" y2="50" />
            <line x1="80" y1="130" x2="200" y2="130" /><line x1="320" y1="130" x2="440" y2="130" />
            <line x1="80" y1="210" x2="200" y2="210" /><line x1="200" y1="210" x2="320" y2="210" />
            <line x1="200" y1="50" x2="200" y2="130" /><line x1="200" y1="130" x2="200" y2="210" />
            <line x1="320" y1="50" x2="320" y2="130" />
            <line x1="440" y1="130" x2="440" y2="210" />
          </g>
          <g className="rg-svg-teal" fill="none">
            <line x1="80" y1="50" x2="80" y2="130" /><line x1="80" y1="130" x2="80" y2="210" />
            <line x1="200" y1="130" x2="320" y2="130" />
            <line x1="320" y1="130" x2="320" y2="210" />
            <line x1="320" y1="210" x2="440" y2="210" />
            <line x1="440" y1="50" x2="440" y2="130" />
          </g>
          <g fill="#707070">
            <circle cx="80" cy="50" r="3" /><circle cx="200" cy="50" r="3" /><circle cx="320" cy="50" r="3" /><circle cx="440" cy="50" r="3" />
            <circle cx="80" cy="130" r="3" /><circle cx="200" cy="130" r="3" /><circle cx="320" cy="130" r="3" /><circle cx="440" cy="130" r="3" />
            <circle cx="80" cy="210" r="3" /><circle cx="200" cy="210" r="3" /><circle cx="320" cy="210" r="3" /><circle cx="440" cy="210" r="3" />
          </g>
          <text className="rg-svg-ts" x="80" y="248">{t("struct.reg.retea_muchii_candidate")}</text>
          <text className="rg-svg-tm" x="530" y="112">x = [ 1 1 0 0 1 0 1 1 0 1 0 1 … ]</text>
          <text className="rg-svg-ts" x="530" y="140">{t("struct.reg.bit_per_muchie")}</text>
          <text className="rg-svg-ts" x="530" y="158">{t("struct.reg.svg_1_perete_activat")}</text>
          <text className="rg-svg-ts" x="530" y="176">{t("struct.reg.0_fara_perete")}</text>
          <line className="rg-svg-dash" x1="460" y1="130" x2="516" y2="112" />
          <text className="rg-svg-ts" x="530" y="212">{t("struct.reg.svg_toate_constrangerile")}</text>
          <text className="rg-svg-ts" x="530" y="230">{t("struct.reg.rezistenta_accesibilitat")}</text>
          <text className="rg-svg-ts" x="530" y="248">{t("struct.reg.functii_vector")}</text>
        </svg>
        <figcaption className="rg-figcap">
          <b>{t("struct.reg.structura_baza")}</b> {t("struct.reg.formularea_publicata_zha")}
        </figcaption>
      </figure>

      <h4 className="rg-h4">{t("struct.reg.foloseste_peste_structur")}</h4>
      <table className="sg-table" style={{ marginBottom: 12 }}>
        <thead>
          <tr><th style={{ width: "30%" }}>{t("struct.reg.th_metoda")}</th><th>{t("struct.reg.rezolva_raporteaza")}</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>{t("struct.reg.algoritmi_evolutivi")}</td>
            <td>{t("struct.reg.cea_raspandita_alegere")}</td>
          </tr>
          <tr>
            <td>{t("struct.reg.cautare_tabu_functie")}</td>
            <td>{t("struct.reg.aplicata_cod_binar")}</td>
          </tr>
          <tr>
            <td>{t("struct.reg.doua_etape_topologie")}</td>
            <td>{t("struct.reg.intai_algoritm_genetic")}</td>
          </tr>
          <tr>
            <td>{t("struct.reg.extragere_candidati_bim")}</td>
            <td>{t("struct.reg.pozitiile_posibile_peret")}</td>
          </tr>
          <tr>
            <td>{t("struct.reg.modele_surogat_invatate")}</td>
            <td>{t("struct.reg.retele_neuronale_graf")}</td>
          </tr>
        </tbody>
      </table>

      <div className="rg-key">
        <span className="rg-lbl">{t("struct.reg.simplificarea_face_probl")}</span>
        <p className="rg-p">
          {tx("struct.reg.constrangerile_par_neliniare")}
        </p>
        <pre className="rg-pre">{`excentricitate:  | Σ x_i·k_i·(X_i − X_CM) |  ≤  eps · L · Σ x_i·k_i
torsiune:          Σ x_i·k_i·(X_i − X_CM)²   ≥  l_s² · Σ x_i·k_i
drift:             Σ x_i·I_i                 ≥  I_req`}</pre>
        <p className="rg-p" style={{ marginBottom: 0 }}>
          {t("struct.reg.coeficientii_constante_g")}
        </p>
      </div>

      <h4 className="rg-h4">{t("struct.reg.arhitectura_recomandata")}</h4>
      <pre className="rg-pre">{t("struct.reg.arhitectura_pipeline")}</pre>
      <p className="rg-p">
        {t("struct.reg.ratiunea_impartirii_meta")}
      </p>
    </div>
  );
}
